import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';
import { RequestState } from '../../shared/types/request-state';
import {
  getTasksRequest,
  getTaskStatusCountRequest,
  updateTasksRequest,
  getTaskerDataRequest,
  updateTaskNoteRequest,
  bulkTasksUpdateRequest,
  getShareableTaskerDataRequest,
  getUndoTasksRequest,
  getWhatsappLinkRequest,
  createTaskRequest,
  getFailedTasksRequest,
} from './extra-actions';
import {
  ProspectFilterResetArg,
  ProspectsFilters,
} from '../../shared/types/prospects-filters';
import {
  getTasksFiltersFromLocalStore,
  removeTasksFiltersFromLocalStore,
  setTasksFiltersInLocalStore,
} from './components/tasks-content/utils/helper';
import { BulkSelectedTasks } from './utils/types';

export const tasksFiltersInitialState: ProspectsFilters = {
  conversationType: [],
  tags: [],
  owners: [],
  status: [],
  outcomes: [],
  sequences: [],
  'verification status': [],
  'task priority': [],
  'created date': [],
  'creation source': [],
  'current step': [],
  'email account': [],
  'client associated': [],
  'imported file': [],
  subSequenceIds: [],
  subSequenceStepIds: [],
  callOutcomes: [],
};

interface State {
  getTasksRequest: RequestState;
  getUndoTasksRequest: RequestState;
  getTaskStatusCountRequest: RequestState;
  updateTasksRequest: RequestState;
  bulkTasksUpdateRequest: RequestState;
  getTaskerDataRequest: RequestState;
  getShareableTaskerDataRequest: RequestState;
  updateTaskNoteRequest: RequestState;
  getWhatsappLinkRequest: RequestState;
  createTaskRequest: RequestState;
  getFailedTasksRequest: RequestState;
  failedTasks: any[];
  failedTasksCount: number;
  tasks: any[];
  prospectTasks: any[];
  undoTasks: any[];
  taskStatusCount: any[];
  tasksCount: number;
  tasksFilters: ProspectsFilters;
  selectedTasks: any[];
  taskerData: any[];
  shareableTaskerData: any;
  bulkSelectedTasks: BulkSelectedTasks;
  isFirstTimeUser: boolean;
  getWhatsappLinkResponse?: string;
}

const initialState: State = {
  getTasksRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getUndoTasksRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTaskStatusCountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateTasksRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  bulkTasksUpdateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTaskerDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getShareableTaskerDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateTaskNoteRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getWhatsappLinkRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createTaskRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getFailedTasksRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  failedTasks: [],
  failedTasksCount: 0,
  tasks: [],
  prospectTasks: [],
  undoTasks: [],
  taskStatusCount: null,
  tasksCount: null,
  tasksFilters: getTasksFiltersFromLocalStore() || tasksFiltersInitialState,
  selectedTasks: [],
  taskerData: [],
  shareableTaskerData: [],
  bulkSelectedTasks: null,
  isFirstTimeUser: false,
  getWhatsappLinkResponse: null,
};

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    onBulkSelectTasks: (state, action) => {
      state.bulkSelectedTasks = action?.payload;
    },
    clearTasksSelection: (state) => {
      state.bulkSelectedTasks = initialState?.bulkSelectedTasks;
      state.selectedTasks = initialState?.selectedTasks;
    },
    checkSingle: (state, action) => {
      const taskId = action?.payload?.row?.id;

      if (action.payload.status === true) {
        // If bulk mode is on, remove this task from the deSelected list
        if (state?.bulkSelectedTasks?.isAllTasksSelected) {
          state.bulkSelectedTasks.deselectedTaskIds = state?.bulkSelectedTasks?.deselectedTaskIds?.filter(
            (id) => id !== taskId,
          );
        }

        // Add the task to selectedTasks
        state.selectedTasks = state?.selectedTasks?.concat([
          action?.payload?.row,
        ]);
      } else {
        // If bulk mode is on, track this as deselected
        if (state?.bulkSelectedTasks?.isAllTasksSelected) {
          state.bulkSelectedTasks.deselectedTaskIds = state?.bulkSelectedTasks?.deselectedTaskIds?.concat(
            [taskId],
          );
        }

        // Remove task from selectedTasks
        state.selectedTasks = state?.selectedTasks?.filter(
          ({ id }) => id !== taskId,
        );
      }
    },
    checkAll: (state, action) => {
      if (action.payload.status === true) {
        // If bulk is selected, remove deselected taskIds from tracking
        if (state?.bulkSelectedTasks?.isAllTasksSelected) {
          action?.payload?.rows?.forEach(({ id: taskId }) => {
            state.bulkSelectedTasks.deselectedTaskIds = state?.bulkSelectedTasks?.deselectedTaskIds?.filter(
              (id) => id !== taskId,
            );
          });
        }

        // Add selected tasks
        state.selectedTasks = state?.selectedTasks?.concat(
          action?.payload?.rows,
        );
      } else {
        // If bulk is selected, track de-selections
        if (state?.bulkSelectedTasks?.isAllTasksSelected) {
          action?.payload?.rows?.forEach(({ id }) => {
            state.bulkSelectedTasks.deselectedTaskIds = state?.bulkSelectedTasks?.deselectedTaskIds?.concat(
              [id],
            );
          });
        }

        // Remove unselected tasks from selected list
        state.selectedTasks = state?.selectedTasks?.reduce((acc, item) => {
          const isPresent = action?.payload?.rows?.some(
            ({ id }) => id === item.id,
          );
          return isPresent ? acc : [...acc, item];
        }, []);
      }
    },
    resetSelectedTasks: (state) => {
      state.selectedTasks = initialState.selectedTasks;
    },
    resetTasksState: (state) => {
      state.updateTasksRequest = initialState.updateTasksRequest;
    },
    resetBulkTasksState: (state) => {
      state.bulkTasksUpdateRequest = initialState.bulkTasksUpdateRequest;
    },
    resetUndoTasksState: (state) => {
      state.getUndoTasksRequest = initialState.getUndoTasksRequest;
    },
    resetTaskerState: (state) => {
      state.getTaskerDataRequest = initialState.getTaskerDataRequest;
    },
    resetShareableTaskerState: (state) => {
      state.getShareableTaskerDataRequest =
        initialState.getShareableTaskerDataRequest;
    },
    setTasksFilters: (state, action) => {
      state.tasksFilters = { ...action.payload };
      setTasksFiltersInLocalStore(action.payload);
    },
    resetGetTasksState: (state) => {
      state.getTasksRequest = initialState.getTasksRequest;
      state.tasks = initialState.tasks;
      state.prospectTasks = initialState.prospectTasks;
      state.tasksCount = initialState.tasksCount;
      state.isFirstTimeUser = initialState.isFirstTimeUser;
    },
    resetGetTasksRequest: (state) => {
      state.getTasksRequest = initialState.getTasksRequest;
    },
    resetGetWhatsappLinkState: (state) => {
      state.getWhatsappLinkRequest = initialState.getWhatsappLinkRequest;
      state.getWhatsappLinkResponse = initialState.getWhatsappLinkResponse;
    },
    resetTasksFilters: (state, action) => {
      const resetOff: ProspectFilterResetArg = action.payload;
      if (resetOff === 'all') {
        state.tasksFilters = tasksFiltersInitialState;
        removeTasksFiltersFromLocalStore();
      } else {
        state.tasksFilters[resetOff] = [];
        setTasksFiltersInLocalStore(state.tasksFilters);
      }
    },
    resetCreateTaskRequest: (state) => {
      state.createTaskRequest = initialState.createTaskRequest;
    },
    resetGetFailedTasksRequest: (state) => {
      state.getFailedTasksRequest = initialState.getFailedTasksRequest;
    },
    removeTaskFromTaskerData: (state, action) => {
      state.taskerData = state.taskerData.filter(
        (task) => task.id !== action.payload.taskId,
      );
    },
    insertTaskIntoTaskerData: (state, action) => {
      const { task, index } = action?.payload;
      if (index >= 0 && index <= state.taskerData?.length) {
        state.taskerData.splice(index, 0, task);
      }
    },
  },
  extraReducers: (builder) => {
    // Get Tasks Status Count
    builder.addCase(getTaskStatusCountRequest.pending, (state) => {
      state.getTaskStatusCountRequest.status = RequestStatus.Pending;
      state.getTaskStatusCountRequest.error = null;
    });
    builder.addCase(getTaskStatusCountRequest.fulfilled, (state, action) => {
      state.getTaskStatusCountRequest.status = RequestStatus.Succeeded;
      state.getTaskStatusCountRequest.message = action.payload.message;
      state.taskStatusCount = action?.payload?.payload;
    });
    builder.addCase(getTaskStatusCountRequest.rejected, (state, action) => {
      state.getTaskStatusCountRequest.status = RequestStatus.Failed;
      state.getTaskStatusCountRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Tasks
    builder.addCase(getTasksRequest.pending, (state) => {
      state.getTasksRequest.status = RequestStatus.Pending;
      state.getTasksRequest.error = null;
    });
    builder.addCase(getTasksRequest.fulfilled, (state, action) => {
      state.getTasksRequest.status = RequestStatus.Succeeded;
      state.getTasksRequest.message = action.payload.message;

      // Only update state.tasks if this is NOT a prospect-specific request
      // This prevents prospect modal task calls from affecting the main tasks list
      const isProspectSpecificRequest =
        action.meta.arg?.prospectId !== undefined;

      if (!isProspectSpecificRequest) {
        state.tasks = action.payload.payload?.data;
        state.tasksCount = action?.payload?.payload?.count;
        state.isFirstTimeUser = action?.payload?.payload?.isFirstTimeUser;
      }

      // Always update prospectTasks for prospect-specific requests
      if (isProspectSpecificRequest) {
        state.prospectTasks = action?.payload?.payload?.data;
      }
    });
    builder.addCase(getTasksRequest.rejected, (state, action) => {
      state.getTasksRequest.status = RequestStatus.Failed;
      state.getTasksRequest.error = !action.payload.isHandled && action.payload;
    });

    // Get Undo Tasks
    builder.addCase(getUndoTasksRequest.pending, (state) => {
      state.getUndoTasksRequest.status = RequestStatus.Pending;
      state.getUndoTasksRequest.error = null;
    });
    builder.addCase(getUndoTasksRequest.fulfilled, (state, action) => {
      state.getUndoTasksRequest.status = RequestStatus.Succeeded;
      state.getUndoTasksRequest.message = action.payload.message;
      state.undoTasks = action?.payload?.payload?.data;
    });
    builder.addCase(getUndoTasksRequest.rejected, (state, action) => {
      state.getUndoTasksRequest.status = RequestStatus.Failed;
      state.getUndoTasksRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Tasks
    builder.addCase(updateTasksRequest.pending, (state) => {
      state.updateTasksRequest.status = RequestStatus.Pending;
      state.updateTasksRequest.error = null;
    });
    builder.addCase(updateTasksRequest.fulfilled, (state, action) => {
      state.updateTasksRequest.status = RequestStatus.Succeeded;
      state.updateTasksRequest.message = action.payload.message;
    });
    builder.addCase(updateTasksRequest.rejected, (state, action) => {
      state.updateTasksRequest.status = RequestStatus.Failed;
      state.updateTasksRequest.message = action.payload.message;
      state.updateTasksRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Bulk Update Tasks
    builder.addCase(bulkTasksUpdateRequest.pending, (state) => {
      state.bulkTasksUpdateRequest.status = RequestStatus.Pending;
      state.bulkTasksUpdateRequest.error = null;
    });
    builder.addCase(bulkTasksUpdateRequest.fulfilled, (state, action) => {
      state.bulkTasksUpdateRequest.status = RequestStatus.Succeeded;
      state.bulkTasksUpdateRequest.message =
        action.payload.payload.message?.message ?? '';
    });
    builder.addCase(bulkTasksUpdateRequest.rejected, (state, action) => {
      state.bulkTasksUpdateRequest.status = RequestStatus.Failed;
      state.bulkTasksUpdateRequest.message = action.payload.message;
      state.bulkTasksUpdateRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Tasker Data
    builder.addCase(getTaskerDataRequest.pending, (state) => {
      state.getTaskerDataRequest.status = RequestStatus.Pending;
      state.getTaskerDataRequest.error = null;
    });
    builder.addCase(getTaskerDataRequest.fulfilled, (state, action) => {
      state.getTaskerDataRequest.status = RequestStatus.Succeeded;
      state.getTaskerDataRequest.message = action.payload.message;
      state.taskerData = action.payload.payload;
    });
    builder.addCase(getTaskerDataRequest.rejected, (state, action) => {
      state.getTaskerDataRequest.status = RequestStatus.Failed;
      state.getTaskerDataRequest.message = action.payload.message;
      state.getTaskerDataRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Shareable Tasker Data
    builder.addCase(getShareableTaskerDataRequest.pending, (state) => {
      state.getShareableTaskerDataRequest.status = RequestStatus.Pending;
      state.getShareableTaskerDataRequest.error = null;
    });
    builder.addCase(
      getShareableTaskerDataRequest.fulfilled,
      (state, action) => {
        state.getShareableTaskerDataRequest.status = RequestStatus.Succeeded;
        state.getShareableTaskerDataRequest.message = action.payload.message;
        state.shareableTaskerData = action?.payload?.payload;
      },
    );
    builder.addCase(getShareableTaskerDataRequest.rejected, (state, action) => {
      state.getShareableTaskerDataRequest.status = RequestStatus.Failed;
      state.getShareableTaskerDataRequest.message = action.payload.message;
      state.getShareableTaskerDataRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Task Note
    builder.addCase(updateTaskNoteRequest.pending, (state) => {
      state.updateTaskNoteRequest.status = RequestStatus.Pending;
      state.updateTaskNoteRequest.error = null;
    });
    builder.addCase(updateTaskNoteRequest.fulfilled, (state, action) => {
      state.updateTaskNoteRequest.status = RequestStatus.Succeeded;
      state.updateTaskNoteRequest.message = action.payload.message;
    });
    builder.addCase(updateTaskNoteRequest.rejected, (state, action) => {
      state.updateTaskNoteRequest.status = RequestStatus.Failed;
      state.updateTaskNoteRequest.message = action.payload.message;
      state.updateTaskNoteRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Whatsapp Link
    builder.addCase(getWhatsappLinkRequest.pending, (state) => {
      state.getWhatsappLinkRequest.status = RequestStatus.Pending;
      state.getWhatsappLinkRequest.error = null;
    });
    builder.addCase(getWhatsappLinkRequest.fulfilled, (state, action) => {
      state.getWhatsappLinkRequest.status = RequestStatus.Succeeded;
      state.getWhatsappLinkRequest.message = action.payload.message;
      state.getWhatsappLinkResponse = action?.payload?.payload?.redirectUrl;
    });
    builder.addCase(getWhatsappLinkRequest.rejected, (state, action) => {
      state.getWhatsappLinkRequest.status = RequestStatus.Failed;
      state.getWhatsappLinkRequest.message = action.payload.message;
      state.getWhatsappLinkRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Create Task
    builder.addCase(createTaskRequest.pending, (state) => {
      state.createTaskRequest.status = RequestStatus.Pending;
      state.createTaskRequest.error = null;
    });
    builder.addCase(createTaskRequest.fulfilled, (state, action) => {
      state.createTaskRequest.status = RequestStatus.Succeeded;
      state.createTaskRequest.message = action.payload.payload.message;
    });
    builder.addCase(createTaskRequest.rejected, (state, action) => {
      state.createTaskRequest.status = RequestStatus.Failed;
      state.createTaskRequest.message = action.payload.message;
      state.createTaskRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Failed Tasks
    builder.addCase(getFailedTasksRequest.pending, (state) => {
      state.getFailedTasksRequest.status = RequestStatus.Pending;
      state.getFailedTasksRequest.error = null;
    });
    builder.addCase(getFailedTasksRequest.fulfilled, (state, action) => {
      state.getFailedTasksRequest.status = RequestStatus.Succeeded;
      state.getFailedTasksRequest.message = action.payload.message;
      // TODO: remove dummy-data guard before production
      const tasks = action?.payload?.payload?.tasks;
      const total = action?.payload?.payload?.total;
      if (Array.isArray(tasks)) {
        state.failedTasks = tasks;
        state.failedTasksCount = typeof total === 'number' ? total : tasks.length;
      }
    });
    builder.addCase(getFailedTasksRequest.rejected, (state) => {
      state.getFailedTasksRequest.status = RequestStatus.Failed;
      // keep seeded dummy data intact when API is unavailable
    });
  },
});

export const {
  onBulkSelectTasks,
  checkSingle,
  checkAll,
  resetTasksState,
  resetBulkTasksState,
  resetUndoTasksState,
  setTasksFilters,
  resetTasksFilters,
  resetSelectedTasks,
  removeTaskFromTaskerData,
  resetTaskerState,
  resetShareableTaskerState,
  clearTasksSelection,
  insertTaskIntoTaskerData,
  resetGetTasksState,
  resetGetTasksRequest,
  resetGetWhatsappLinkState,
  resetCreateTaskRequest,
  resetGetFailedTasksRequest,
} = tasksSlice.actions;
export default tasksSlice.reducer;
